import { FilterContext, FilterProvider, FilterProviderRef, FilterValues } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import GroupByPeriodFilter from "components/filters/Filters/GroupByPeriodFilter";
import { getCacheFriendlyInstruction } from "components/filters/Filters/InstructionHelpers";
import SingleBookkeepingFilter from "components/filters/Filters/SingleBookkeepingFilter";
import { mapGroupByPeriodFilterToInstruction, mapResponseToSingleBookkeepingFilter, mapResponseToGroupByPeriodFilter } from "components/filters/mappingUtils";
import { NoDataAvailableWarning } from "components/NoDataAvailableDiv";
import { Loading, Panel } from "components/panels";
import Table from "components/Tables/Table";
import React, { useContext, useState } from "react";
import { useLoaderData } from "react-router";
import { getDocumentCoreApiExceptInvoice } from "utilities/apiUtils";
import { canReadCosts, canReadSales, getPluralLabelForDocumentType } from "utilities/documentUtils";
import { getMoneyFormatter } from "utilities/formatter";
import { useCustomQuery } from "utilities/hookutils";
import { DocumentChart } from "./SalesChart";

interface PageData {
  viewModel: server.viewmodel.DocumentTotalFrontModel
  documentType: server.dto.DocumentTypes
}

const mapFilterValuesToInstruction = (values: FilterValues): server.dto.ListDocumentTotalInstruction => ({
  bookkeepingId: values.bookkeepings.selectedBookkeepings.length > 0 ? values.bookkeepings.selectedBookkeepings[0].bookkeepingId : undefined,
  period: mapGroupByPeriodFilterToInstruction(values.groupByPeriod),
});

const mapResponseToFilterValues = (response: server.dto.ListDocumentTotalFilters): Partial<FilterValues> => ({
  bookkeepings: mapResponseToSingleBookkeepingFilter(response.bookkeeping),
  groupByPeriod: mapResponseToGroupByPeriodFilter(response.period)
});

export const DocumentTotalsApp = () => {
  const { viewModel, documentType } = useLoaderData<PageData>();
  const filterRef = React.useRef<FilterProviderRef>(null);

  const getFilterSet = (): server.dto.FilterSets => {
    if (documentType === "contract") {
      return "contract_Totals";
    } else if (documentType === "purchaseAgreement") {
      return "purchaseAgreement_Totals";
    } else if (documentType === "quote") {
      return "quote_Totals";
    }
  }

  const [instruction, onInstructionChange] = useState<server.dto.ListDocumentTotalInstruction>({
    ...viewModel.initialInstruction,
    cache: getFilterSet()
  });
  const [initialLoading, setInitialLoading] = useState(true);

  const api = useCustomQuery({
    queryFn: async () => {
      return getDocumentCoreApiExceptInvoice(documentType)
        .GetDocumentTotals(getCacheFriendlyInstruction(instruction));
    },
    enabled: !!instruction.bookkeepingId,
    queryKey: ["documentTotals", documentType, instruction],
    onSuccess: (response) => {
      if (initialLoading) {
        filterRef.current.setDefaultValues(() => {
          return mapResponseToFilterValues(viewModel.defaultFilters);
        });
      }
      filterRef.current.updateValues(() => {
        return mapResponseToFilterValues(response.data.request);
      });
      setInitialLoading(false);
    },
    cacheTime: 0
  });

  const onFilterChange = (filter: FilterValues) => {
    const newInstruction = mapFilterValuesToInstruction(filter);
    onInstructionChange({
      ...newInstruction,
      cache: getFilterSet()
    });
  };

  return <FilterProvider
    ref={filterRef}
    onFilterChange={onFilterChange}
  >
    <div className="flex-column panel-gap">
      <FilterHeader.Filters>
        <div className="selectiontool-header-filters">
          <SingleBookkeepingFilter initialBookkeeping={viewModel.defaultFilters.bookkeeping} />
          <GroupByPeriodFilter />
        </div>
      </FilterHeader.Filters>

      <TableAndChart
        items={api.data?.data.data ?? []}
        isLoading={api.isLoading}
        documentType={documentType}
      />
    </div>
  </FilterProvider>
}

interface TableAndChartProps {
  items: server.dto.GroupedDocumentTotals[]
  isLoading: boolean
  documentType: server.dto.DocumentTypes
}

const TableAndChart = (props: TableAndChartProps) => {
  const filterContext = useContext(FilterContext);
  const moneyFormatter = getMoneyFormatter();

  const selectedBookkeepings = filterContext.getValue("bookkeepings").selectedBookkeepings;
  const selectedBookkeeping = selectedBookkeepings.length > 0 ? selectedBookkeepings[0] : null;
  const currency = selectedBookkeeping ? selectedBookkeeping.baseCurrency : "";

  if (!selectedBookkeeping) {
    return <NoDataAvailableWarning>
      {RESX.Bookkeeping.SelectABookkeeping}
    </NoDataAvailableWarning>;
  }

  if (props.isLoading) {
    return <Loading />;
  }

  const displaySalesExcludingTax = canReadSales(props.documentType);
  const displaySalesIncludingTax = canReadSales(props.documentType);
  const displayCostExcludingTax = canReadCosts(props.documentType);
  const displayChart = canReadSales(props.documentType) || canReadCosts(props.documentType);

  const totalCount = props.items.reduce((acc, item) => acc + item.count, 0);
  const totalSalesExcludingTax = props.items.reduce((acc, item) => acc + item.totalSalesExcludingTax, 0);
  const totalSalesIncludingTax = props.items.reduce((acc, item) => acc + item.totalSalesIncludingTax, 0);
  const totalCostExcludingTax = props.items.reduce((acc, item) => acc + item.totalCostExcludingTax, 0);

  const getFooterEntries = () => {
    const entries = [
      null,
      <div key="count">{totalCount} {getPluralLabelForDocumentType(props.documentType)}</div>,
      <div key="totalCostExcludingTax" className="s1_cell_financialamount">{moneyFormatter.format(totalCostExcludingTax)} {currency.toUpperCase()}</div>,
      <div key="totalSalesExcludingTax" className="s1_cell_financialamount">{moneyFormatter.format(totalSalesExcludingTax)} {currency.toUpperCase()}</div>,
      <div key="totalSalesIncludingTax" className="s1_cell_financialamount">{moneyFormatter.format(totalSalesIncludingTax)} {currency.toUpperCase()}</div>,
    ];

    return entries;
  }

  return <>
    <Panel
      panelClassName="s1_panel"
      title={selectedBookkeeping.name}
    >
      <Table
        items={props.items}
        isLoading={props.isLoading}
        columns={[{
          key: "period",
          header: RESX.DateTime.Period,
          render: item => item.label,
          size: "minmax(auto, 1fr)",
        }, {
          key: "count",
          header: RESX.Document.NumberOfDocuments,
          render: item => <>{item.count} {getPluralLabelForDocumentType(props.documentType)}</>,
          size: "minmax(auto, 1fr)",
        }, {
          key: "totalCostExcludingTax",
          header: RESX.Financial.TotalCostsExTax,
          className: "s1_cell_financialamount",
          hidden: !displayCostExcludingTax,
          render: item => <>{moneyFormatter.format(item.totalCostExcludingTax)} {currency.toUpperCase()}</>,
          size: "minmax(auto, 1fr)",
        }, {
          key: "totalSalesExcludingTax",
          header: RESX.Financial.TotalSalesExTax,
          className: "s1_cell_financialamount",
          hidden: !displaySalesExcludingTax,
          render: item => <>{moneyFormatter.format(item.totalSalesExcludingTax)} {currency.toUpperCase()}</>,
          size: "minmax(auto, 1fr)",
        }, {
          key: "totalSalesIncludingTax",
          header: RESX.Financial.TotalSalesIncTax,
          className: "s1_cell_financialamount",
          hidden: !displaySalesIncludingTax,
          render: item => <>{moneyFormatter.format(item.totalSalesIncludingTax)} {currency.toUpperCase()}</>,
          size: "minmax(auto, 1fr)",
        }]}
        footers={[{
          key: "total",
          className: "panel-header-bg",
          entries: getFooterEntries(),
        }]}
        getKey={i => i.label}
      />
    </Panel>

    {displayChart && <Panel
      panelClassName="s1_panel"
      title={RESX.Financial.Total}
    >
      <DocumentChart
        period={filterContext.getValue("groupByPeriod").groupBy}
        documentType={props.documentType}
        documentTotals={props.items}
        currency={currency}
      />
    </Panel>}
  </>
}